<template>
  <template v-if="showItem(item)">
    <li :class="['MainMenuLink', isLastItem ? '' : isSubMenu ? 'tw:mb-1' : 'tw:mb-1', 'tw:relative']" v-bind="$attrs">
      <MainMenuLinkButton
        :active="active"
        :active-children="activeChildren"
        :is-shrink="!visible"
        :is-sub-menu-item="isSubMenu"
        :item="item"
        :show-children="showChildren"
        @click="handleClickMenuItem()"
      >
        <template v-if="badge" #badge>
          <EdroneBadge
            v-if="item.name === 'inbox'"
            :label="badge"
            :emphasis="badge !== '0' ? 'high' : 'low'"
            size="small"
            type="info"
          />
          <EdroneBadge
            v-else-if="item.name === 'segments'"
            :label="badge"
            :emphasis="badge !== '0' ? 'high' : 'low'"
            size="small"
            type="info"
          />
          <EdroneBadge
            v-else-if="item.name === 'engagements'"
            :label="badge"
            :type="badge?.startsWith('0') ? 'default' : 'success'"
            size="small"
          />
          <EdroneBadge
            v-else-if="item.name === 'custom-flows'"
            :label="badge"
            :type="badge?.startsWith('0') ? 'default' : 'success'"
            size="small"
          />
          <EdroneBadge
            v-else
            :icon="
              badge === '1' || badge === 'ACTIVE'
                ? 'fa-light fa-check'
                : badge === 'EXPIRED'
                ? 'fa-light fa-hourglass-end'
                : 'fa-light fa-minus'
            "
            :type="badge === '1' || badge === 'ACTIVE' ? 'success' : badge === 'EXPIRED' ? 'warning' : 'default'"
            size="small"
          />
        </template>
      </MainMenuLinkButton>
      <transition
        enter-active-class="tw:transition-all tw:duration-[300ms] tw:ease-in-out"
        enter-from-class="tw:max-h-0"
        enter-to-class="tw:max-h-96"
        leave-active-class="tw:transition-all tw:duration-[300ms] tw:ease-in-out"
        leave-from-class="tw:max-h-96"
        leave-to-class="tw:max-h-0"
      >
        <template v-if="item.children?.length && showChildren && visible">
          <div class="tw:flex tw:gap-1.5 tw:relative tw:overflow-y-hidden">
            <div class="tw:w-px tw:mt-2 tw:bg-default-200 tw:ms-4"></div>
            <ul class="tw:m-0 tw:px-0 tw:pb-0 tw:pt-2 tw:max-w-[calc(100%-1.5rem)]">
              <MainMenuLink
                v-for="(child, index) in item.children"
                :key="item.name + '-' + index"
                :is-last-item="index === item.children.length - 1"
                :item="child"
                :menu-badges="menuBadges"
                class="tw:relative"
                is-sub-menu
              />
            </ul>
          </div>
        </template>
      </transition>
    </li>
  </template>
</template>

<script lang="ts" setup>
import { computed, onMounted, PropType } from "vue";
import { useRoute } from "vue-router";
import MainMenuLinkButton from "./MainMenuLinkButton.vue";
import { MenuItem, isActive, getBadgeText } from "./MainMenu.helpers.ts";
import { gtagUniversalSend } from "vue-app/plugins/edroneGtag";
import EdroneBadge from "edrone-components/EdroneBadge/EdroneBadge.vue";
import { DTOMenuIndicatorResponse } from "vue-app/api/dto";

const props = defineProps({
  item: {
    type: Object as PropType<MenuItem>,
    required: true,
  },
  visible: {
    type: Boolean,
    default: true,
  },
  isLastItem: {
    type: Boolean,
    default: false,
  },
  activeMenu: {
    type: [String, null],
    required: false,
  },
  menuBadges: {
    type: Object as PropType<DTOMenuIndicatorResponse | null>,
    required: false,
  },
  isSubMenu: {
    type: Boolean,
    default: false,
  },
});
const emit = defineEmits(["update:activeMenu"]);

const route = useRoute();
const isDev = window.EDRONE_CONTEXT.user.isDev;

const active = computed(() => isActive(route.path, props.item));

const activeChildren = computed(() => {
  return props.item.children && check(props.item.children);
});

function check(children: MenuItem[]): boolean {
  return children.some((child) => isActive(route.path, child) || (child.children && check(child.children)));
}

function handleClickMenuItem() {
  gtagUniversalSend({ component_id: "main-menu__navigation-link-button--" + props.item.name });

  if (props.item.disabled && !isDev) {
    return;
  } else if (props.item.children?.length) {
    emit("update:activeMenu", props.item.name === props.activeMenu ? null : props.item.name);
  }
}

const showChildren = computed(() => props.activeMenu === props.item.name);

function showItem(item: MenuItem) {
  return !item.hidden || isDev;
}

onMounted(() => {
  if (route.path === props.item.path || (props.item.children && check(props.item.children))) {
    emit("update:activeMenu", props.item.name);
  }
});

const badge = computed(() => {
  return getBadgeText(props.item.name, props.menuBadges);
});
</script>
